<template>
  <div class="pending-status grid" :class="{ 'empty-grid': (loading || rows.length === 0) }">
    <template v-if="loading">
      <mds-loader />
    </template>
    <template v-else>
      <template v-if="rows.length > 0">
        <DataTable
          :columnsConfig="columnsConfig"
          :data="paginatedData"
          class="table-container fixed-header-table"
        >
          <template v-slot:header-cell="{ content, col }">
            <div v-if="columnsConfig[col].key === 'select'" class="pending-universe__check">
              <template v-if="localPolicyOptions.options['submit-to-production']">
                <PendingCheckBox
                  class="pending-status__select-all--checkbox"
                  :indeterminate="isIndeterminateSelected"
                  :checked="isTotalSelected"
                  @change="selectAllClients"
                />
              </template>
            </div>
            <template v-else>
              {{ content }}
            </template>
          </template>
          <template v-slot:cell="{ content, row, col, sub }">
            <div v-if="columnsConfig[col].key === 'select'" class="pending-universe__check">
              <template v-if="localPolicyOptions.options['submit-to-production']">
                <PendingCheckBox
                  class="pending-status__select-fund--checkbox"
                  v-if="sub === undefined"
                  :indeterminate="paginatedData[row].isIndeterminateSelected"
                  :checked="content"
                  @change="selectFund(...arguments, row)"
                />
              </template>
            </div>
            <!-- SUBS -->
            <div
              v-else-if="columnsConfig[col].key === 'name'"
              :class="{
                fund: sub === undefined,
                client: sub !== undefined,
              }"
            >
              <template>
                <template v-if="localPolicyOptions.options['submit-to-production']">
                  <PendingCheckBox
                    v-if="sub !== undefined && paginatedData[row].subs[sub] !== undefined "
                    :checked="paginatedData[row].subs[sub].select"
                    @change="selectClient(...arguments, row, sub)"
                    class="pending-universe__check pending-status__select-client--checkbox"
                  />
                </template>
                <template v-else>
                  <span :class="{ 'checkbox-space': sub !== undefined }"></span>
                </template>
              </template>
              <template>
                <template v-if="sub === undefined">
                  <CollapseButton
                    class="pending-status__collapse-button"
                    :collapsed="paginatedData[row].__hidden"
                    @toggle="toggleClients(row)"
                  >
                    <span>{{ content }}</span>
                  </CollapseButton>
                </template>
                <template v-else>
                  {{ content }}
                </template>
              </template>
            </div>
            <div v-else-if="columnsConfig[col].key === 'previousStatus'">
              <StatusIcons v-if="content" :status="[{ name: content }]" :fullLength="true" />
            </div>
            <div v-else-if="columnsConfig[col].key === 'updatedStatus'">
              <StatusIcons v-if="content" :status="[{ name: content }]" :fullLength="true" />
            </div>
            <template v-else>{{ content || "—" }}</template>
          </template>
        </DataTable>

        <portal to="pending-status-paginator-portal">
          <div class="pending-status__pager">
            <mds-pagination
              v-if="!isEmptyList"
              show-items-info
              show-items-select
              :page="currentPage"
              :pageSizes="pageSizes"
              :pageSize="pageSize"
              :total-items="totalRows"
              @mds-pagination-page-changed="paginationConfigChanged"
            ></mds-pagination>
          </div>
        </portal>

      </template>
      <template v-else>
        <p class="title-message">Changes to Fund Statuses have not been added</p>
      </template>
    </template>
  </div>
</template>
<script>
import { format } from 'date-fns';
import { mapState, mapActions } from 'vuex';
import {
  groupBy,
  get,
  mapValues,
  cloneDeep,
  orderBy,
  map,
  maxBy,
  minBy,
  sortBy,
  findIndex,
} from 'lodash';

import MdsPagination from '@mds/pagination';
import PendingCheckBox from '@/components/atoms/PendingCheckBox';
import StatusIcons from '@/components/atoms/StatusIcons';
import CollapseButton from '@/components/atoms/CollapseButton';
import DataTable from '@/components/molecules/data-table/table';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import ClientFilterPaginationMixin from '@/mixins/ClientFilterPaginationMixin';

import * as PendingFundsService from '@/services/PendingFunds';
import axios from 'axios';

const columnsConfig = require('@/config/pending-status-changes-columns.json');

const paginationConfig = {
  pageSizes: [10, 25, 50, 100],
  pageSize: 10,
};

export default {
  name: 'PendingGrid',
  components: {
    MdsPagination,
    DataTable,
    PendingCheckBox,
    StatusIcons,
    CollapseButton,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'PendingStatusChangesGrid' }),
    ClientFilterPaginationMixin((data) => data, paginationConfig),
  ],
  data() {
    return {
      columnsConfig,
      rows: [],
      data: [],
      loading: false,
      requestController: null,
      requestUrl: null,
      abortRequestKey: 'AbortRequest',
    };
  },
  mounted() {
    // Sort Listeners
    this.columnsConfig = this.columnsConfig.map((col) => {
      const modifCol = cloneDeep(col);
      if (modifCol.sortDirection !== undefined) {
        modifCol.action = () => this.sortBy(col.key);
      }
      return modifCol;
    });
    // Load Initial Data
    this.loadData();
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.pending.client.selected,
      selectedClientName: (state) => state.pending.client.name,
      searchTerm: (state) => state.pending.searchTerm,
      itemsSelected: (state) => state.pending.itemsSelected,
      selectedTab: (state) => state.pending.tab,
      needsReloading: (state) => state.pending.needsReloading,
    }),
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      return get(this.policies, `pending.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    activeColumn() {
      const [activeColumn] = this.columnsConfig.filter(
        (col) => col.sortDirection !== undefined && col.sortDirection !== 0,
      );
      return activeColumn;
    },
    selectedRows() {
      return this.rows.reduce((items, item) => {
        const subs = item.subs.filter((sub) => sub.select === true);
        if (subs.length > 0) {
          items.push(...subs);
        }
        return items;
      }, []);
    },
    totalSubs() {
      return this.rows.reduce((ac, item) => ac + item.subs.length, 0);
    },
    isIndeterminateSelected() {
      const { selectedRows, totalSubs } = this;
      return selectedRows.length > 0 && selectedRows.length < totalSubs;
    },
    isTotalSelected() {
      const { selectedRows, totalSubs } = this;
      return selectedRows.length > 0 && selectedRows.length === totalSubs;
    },
  },
  methods: {
    ...mapActions([
      'setPendingItemsDisplayed',
      'setPendingItemsSelected',
      'logError',
    ]),
    loadData() {
      if (this.requestController) {
        this.requestController.cancel();
      }

      this.$store.dispatch('setPendingItemsDisplayed', 0);
      this.loading = true;
      this.data = [];
      this.rows = [];
      this.requestController = axios.CancelToken.source();

      PendingFundsService.getPendingFundStatusChangesList({
        selectedFiduciary: this.selectedFiduciary,
        axiosCancelToken: this.requestController?.token,
      }).then((result) => {
        this.currentPage = 1;
        if (get(result, 'status.success')) {
          if (Array.isArray(result.data) && result.data.length > 0) {
            const formatedData = this.formatData(result.data);

            // All the data fetched from the API
            this.data = formatedData || [];
            this.$store.dispatch('setPendingItemsDisplayed', this.data.length);

            // If a filter has been applied we get the filetered data here
            // otherwhise all the data will be stored into the rows
            this.rows = this.filterData(formatedData);
            this.sortData('date', -1);

            // These are the data properties needed to have the pagination feature
            this.listData = this.rows;
            this.filteredList = this.rows;
          }
        }
        this.loading = false;
      }).catch((error) => {
        const httpStatus = (error?.response?.status) ? (error?.response?.status) : error?.status;
        this.loading = false;
        if (httpStatus === 401) {
          this.$router.push('/Login');
        } else if (error.message && error.message !== this.abortRequestKey) {
          this.notifyError({
            error,
            section: 'methods:loadData',
            data: { url: this.requestUrl },
            notificaction: {
              title: 'Pending Status Changes Grid',
              message: `Fetching Data (Error: ${error.message})`,
            },
          });
        }
        return false;
      }).finally(() => {
        this.$store.dispatch('setReloadNeed', false);
      });
    },
    formatData(data) {
      const formattedData = [];
      const groupedData = mapValues(groupBy(data, 'secID'), (v) => sortBy(v, 'client.clientName'));

      try {
        /* eslint-disable-next-line no-restricted-syntax */
        for (const property in groupedData) {
          if (Object.prototype.hasOwnProperty.call(groupedData, property)) {
            const newItem = {
              id: get(groupedData, `[${property}][0].secID`),
              name: get(groupedData, `[${property}][0].fundName`),
              fundName: get(groupedData, `[${property}][0].fundName`),
              ticker: get(groupedData, `[${property}][0].ticker`),
              secid: get(groupedData, `[${property}][0].secID`),
              actions: map(groupedData[property], 'fiduciaryFundID'),
              select: false,
              isIndeterminateSelected: false,
              subs: [],
              __hidden: false,
            };

            /* eslint-disable-next-line no-plusplus */
            for (let i = 0; i < groupedData[property].length; i++) {
              const {
                fiduciaryClient: { client: { clientName: name = '' } = {}, id: clientId = 0 } = {},
              } = groupedData[property][i];
              const hasPendingSeries = get(groupedData, `[${property}][${i}].hasPendingFundSeries`);
              const previousSeriesDefault = hasPendingSeries ? 'None' : null;
              const updatedSeriesDefault = hasPendingSeries ? 'None' : null;
              const newSub = {
                subIndex: i,
                id: `sub-${get(groupedData, `[${property}][${i}].secID`)}-${clientId}`,
                fundId: get(groupedData, `[${property}][${i}].fiduciaryFundID`),
                actions: [get(groupedData, `[${property}][${i}].fiduciaryFundID`)],
                fundName: get(groupedData, `[${property}][0].fundName`),
                name,
                clientId,
                clientName: name,
                hasPendingStatus: get(groupedData, `[${property}][${i}].hasPendingStatus`),
                hasPendingCategory: get(groupedData, `[${property}][${i}].hasPendingCategory`),
                hasPendingFundSeries: hasPendingSeries,
                previousStatus: get(groupedData, `[${property}][${i}].previousStatus.name`),
                updatedStatus: get(groupedData, `[${property}][${i}].updatedStatus.name`),
                previousSeries: get(groupedData, `[${property}][${i}].previousFundSeries.seriesName`, previousSeriesDefault),
                updatedSeries: get(groupedData, `[${property}][${i}].updatedFundSeries.seriesName`, updatedSeriesDefault),
                date: format(get(groupedData, `[${property}][${i}].dateOfChange`), 'MMM D, YYYY'),
                dateOfChange: get(groupedData, `[${property}][${i}].dateOfChange`),
                select: false,
                __hidden: false,
              };
              newItem.subs.push(newSub);
            }
            formattedData.unshift(newItem);
          }
        }
      } catch (error) {
        this.logError({
          errorFrom: 'PendingStatusChangesGrid:methods:formatData',
          details: {
            ...Object.getOwnPropertyDescriptors(error),
            message: error.message,
          },
        });
      }
      return formattedData;
    },
    filterData(formatedData) {
      const rows = cloneDeep(formatedData);
      const filteredByClient = this.filterByClient(rows);
      const filteredByKeyWord = this.filterByKeyword(filteredByClient);
      return filteredByKeyWord;
    },
    filterByClient(rows) {
      if (this.selectedClient !== 0) {
        return rows
          .map((row) => {
            const modifRow = cloneDeep(row);
            modifRow.subs = row.subs.filter((sub) => sub.clientId === this.selectedClient);
            return modifRow;
          })
          .filter((row) => row.subs.length > 0);
      }
      return rows;
    },
    filterByKeyword(rows) {
      if (this.searchTerm) {
        return rows.filter((row) => {
          const matchName = new RegExp(this.searchTerm, 'gi').test(row.name);
          const matchTicker = new RegExp(this.searchTerm, 'gi').test(row.ticker);
          const matchsecID = new RegExp(this.searchTerm, 'gi').test(row.secid);
          let matchClientName = false;
          if (row.subs.length) {
            const foundRows = row.subs.filter((innerrow) => {
              return new RegExp(this.searchTerm, 'gi').test(innerrow.clientName);
            });
            if (foundRows.length) { matchClientName = true; }
          }
          return matchName || matchTicker || matchsecID || matchClientName;
        });
      }
      return rows;
    },
    selectAllClients(checkedValue) {
      /* eslint-disable no-param-reassign */
      this.rows = this.rows.map((item) => {
        item.select = checkedValue;
        item.isIndeterminateSelected = false;
        item.subs = item.subs.map((sub) => {
          sub.select = checkedValue;
          return sub;
        });
        return item;
      });
      this.updateSelectedData();
      /* eslint-enable no-param-reassign */
    },
    selectClient(checkedValue, value, row, sub) {
      const selectedCurrentRow = this.paginatedData[row];
      const actualRow = this.getActualRow(selectedCurrentRow);
      const selectedParentRow = this.rows[actualRow];
      const selectedRow = selectedParentRow.subs[sub];
      const totalSubs = selectedParentRow.subs.length;

      selectedRow.select = checkedValue;
      const totalSelected = selectedParentRow.subs.reduce(
        (ac, el) => ac + (el.select === true ? 1 : 0),
        0,
      );
      if (totalSelected === 0) {
        selectedParentRow.select = false;
        selectedParentRow.isIndeterminateSelected = false;
      } else if (totalSelected === totalSubs) {
        selectedParentRow.select = true;
        selectedParentRow.isIndeterminateSelected = false;
      } else {
        selectedParentRow.select = false;
        selectedParentRow.isIndeterminateSelected = totalSelected > 0 && totalSelected < totalSubs;
      }

      this.updateSelectedData();
    },
    selectFund(checkedValue, value, row) {
      const selectedCurrentRow = this.paginatedData[row];
      const actualRow = this.getActualRow(selectedCurrentRow);
      const selectedRow = this.rows[actualRow];
      selectedRow.select = checkedValue;
      selectedRow.subs = selectedRow.subs.map((item) => {
        const modifItem = cloneDeep(item);
        modifItem.select = checkedValue;
        return modifItem;
      });
      this.updateSelectedData();
    },
    getActualRow(row) {
      const rowIndex = findIndex(this.rows, (item) => {
        return item.id === row.id;
      }, 0);
      return rowIndex;
    },
    updateSelectedData() {
      this.refreshPaginatedData();
      this.$store.dispatch('setPendingItemsSelected', {
        total: this.selectedRows.length,
        list: this.selectedRows.map((item) => item.fundId),
        statusChanges: this.selectedRows.filter((item) => item.hasPendingStatus).length,
        seriesChanges: this.selectedRows.filter((item) => item.hasPendingFundSeries).length,
        categoryChanges: this.selectedRows.filter((item) => item.hasPendingCategory).length,
      });
    },
    sortBy(colKey) {
      /* eslint-disable no-param-reassign */
      let direction = 1;
      this.columnsConfig = this.columnsConfig.map((col) => {
        if (col.key === colKey) {
          if (col.sortDirection !== 0) {
            col.sortDirection *= -1;
          } else {
            col.sortDirection = 1;
          }
          direction = col.sortDirection;
        } else if (col.sortDirection !== undefined) {
          col.sortDirection = 0;
        }
        return col;
      });
      this.sortData(colKey, direction);
      /* eslint-enable no-param-reassign */
    },
    sortData(key, direction) {
      const dir = direction > 0 ? 'asc' : 'desc';
      if (key !== 'date') {
        this.rows = orderBy(this.rows, key, dir);
      } else {
        this.rows = orderBy(
          this.rows,
          (elem) => {
            let time;
            if (direction > 0) {
              time = maxBy(elem.subs, (item) => new Date(item.dateOfChange || item.date).getTime());
            } else {
              time = minBy(elem.subs, (item) => new Date(item.dateOfChange || item.date).getTime());
            }
            return new Date(time.dateOfChange || time.date).getTime();
          },
          dir,
        );
      }
    },
    toggleClients(row) {
      const selectedCurrentRow = this.paginatedData[row];
      const actualRow = this.getActualRow(selectedCurrentRow);
      const isHidden = !this.rows[actualRow].__hidden;
      this.rows[actualRow].__hidden = isHidden;
      this.rows[actualRow].subs = this.rows[actualRow].subs.map((sub) => {
        const modifSub = cloneDeep(sub);
        modifSub.__hidden = isHidden;
        return modifSub;
      });
      this.refreshPaginatedData();
    },
    refreshPaginatedData() {
      this.listData = this.rows;
      this.filteredList = this.filterData(this.listData);
    },
  },
  watch: {
    async selectedClient() {
      await this.loadData();
      this.$store.dispatch('setPendingItemsDisplayed', 0);
      this.$store.dispatch('setPendingItemsSelected', { list: [], total: 0 });
      this.currentPage = 1;
      this.sortData(this.activeColumn.key, this.activeColumn.sortDirection);
    },
    async searchTerm() {
      this.filteredList = this.filterData(this.rows);
      this.$store.dispatch('setPendingItemsDisplayed', 0);
      this.$store.dispatch('setPendingItemsSelected', { list: [], total: 0 });
      this.currentPage = 1;
      this.sortData(this.activeColumn.key, this.activeColumn.sortDirection);
    },
    needsReloading(newValue) {
      if (newValue === true) {
        this.$store.dispatch('setPendingItemsDisplayed', 0);
        this.$store.dispatch('setPendingItemsSelected', { list: [], total: 0 });
        this.currentPage = 1;
        this.loadData();
      }
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.cancel(this.abortRequestKey);
    this.$store.dispatch('setPendingItemsSelected', {
      list: [],
      total: 0,
    });
  },
};
</script>
<style lang="scss">
.pending-status.grid {
  max-width: 200vw;
  width: 120vw;

  &.empty-grid {
    max-width: 100%;
    width: 95%;
  }
}

.pending-universe .grid {
  height: 1px; // Flex fix
  padding: 0 $mds-space-2-x;
}
.pending-universe .mds-data-table td.mds-data-table__header-cell[nowrap] {
  white-space: nowrap;
}
.pending-universe .mds-data-table__header-cell {
  vertical-align: top;
}
.pending-universe .mds-data-table__header-cell[aria-sort] {
  cursor: pointer;
}
.pending-universe .mds-data-table__header-cell-inner {
  padding-top: $mds-space-1-x;
}
.pending-universe .mds-data-table__header-cell-inner[role="button"]:focus {
  box-shadow: none;
}
.pending-universe .mds-data-table__cell {
  vertical-align: middle;
}

// Specific Pending table styles
.pending-universe {
  .fund {
    font-weight: $mds-font-weight-regular;
  }
  .client &__check {
    margin-right: $mds-space-1-x;
    padding-left: $mds-space-quarter-x;
  }
  &__check {
    padding-left: $mds-space-1-x;
  }

  .level-0:hover {
    box-shadow: 0 -2px 4px 0 rgba(70, 60, 60, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    .mds-data-table__cell {
      background: #f2fbff;
    }
  }
  .level-1:hover {
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    .mds-data-table__cell {
      background: #f7fffd;
    }
  }
}

// Fix Header
.fixed-header-table {
  position: relative;
}
.fixed-header-table .mds-data-table__header th {
  background-color: $mds-color-white;
}
.fixed-header-table .mds-data-table__header th {
  position: sticky;
  top: 0;
  z-index: 100;
}

.title-message {
  @include mds-level-1-heading();
  font-weight: $mds-font-weight-thin;
  color: $mds-text-color-primary-on-light;
  margin: $mds-space-8-x auto;
  text-align: center;
  width: 50%;
}

</style>

<style lang="scss" module>
:global(.pending-status__pager) {
  margin: $mds-space-2-x;
  .mds-select__open-indicator {
    bottom: 7px;
  }
}
</style>
